import React, { useState, useEffect, memo, useMemo } from 'react';
import { logger } from '../utils/logger';

const TimeManagement = memo(({ child }) => {
  const [timeRange, setTimeRange] = useState('week'); // 'day', 'week', 'month', 'all'

  // Calculate time spent from progress data
  const timeData = useMemo(() => {
    if (!child?.progress?.completedSubjects) {
      return {
        total: 0,
        bySubject: {},
        byDay: {}
      };
    }

    const bySubject = {};
    const byDay = {};
    let total = 0;

    Object.entries(child.progress.completedSubjects).forEach(([subject, data]) => {
      const timeSpent = data.timeSpent || 0;
      bySubject[subject] = timeSpent;
      total += timeSpent;

      // Group by day if we have lastPlayed data
      if (data.lastPlayed) {
        const date = new Date(data.lastPlayed).toLocaleDateString();
        byDay[date] = (byDay[date] || 0) + timeSpent;
      }
    });

    return { total, bySubject, byDay };
  }, [child]);

  // Format time
  const formatTime = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Get subject display name
  const getSubjectDisplayName = (subject) => {
    const names = {
      phonics: 'Phonics',
      math: 'Math',
      reading: 'Reading',
      spelling: 'Spelling',
      science: 'Science',
      social: 'Social Studies',
      art: 'Art',
      geography: 'Geography',
      history: 'History',
      skipcounting: 'Skip Counting'
    };
    return names[subject] || subject.charAt(0).toUpperCase() + subject.slice(1);
  };

  // Calculate recommended daily time (15-30 minutes for young learners)
  const recommendedDaily = 20; // minutes
  const daysActive = Object.keys(timeData.byDay).length || 1;
  const averageDaily = timeData.total / daysActive;

  if (!child) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a child to view time management
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>⏱️</span>
          <span>Time Management</span>
        </h3>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Total Time</div>
          <div className="text-2xl font-bold text-purple-600">
            {formatTime(timeData.total)}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Average Daily</div>
          <div className="text-2xl font-bold text-green-600">
            {formatTime(averageDaily)}
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Recommended</div>
          <div className="text-2xl font-bold text-blue-600">
            {formatTime(recommendedDaily)}
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Daily Learning Goal</span>
          <span className="text-sm text-gray-600">
            {Math.round((averageDaily / recommendedDaily) * 100)}% of recommended
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${
              averageDaily >= recommendedDaily ? 'bg-green-500' :
              averageDaily >= recommendedDaily * 0.7 ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
            style={{ width: `${Math.min((averageDaily / recommendedDaily) * 100, 100)}%` }}
          />
        </div>
        {averageDaily < recommendedDaily && (
          <p className="text-xs text-gray-500 mt-1">
            Try to reach {formatTime(recommendedDaily)} per day for optimal learning
          </p>
        )}
      </div>

      {/* Time by Subject */}
      {Object.keys(timeData.bySubject).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Time by Subject</h4>
          <div className="space-y-3">
            {Object.entries(timeData.bySubject)
              .sort((a, b) => b[1] - a[1])
              .map(([subject, time]) => {
                const percentage = (time / timeData.total) * 100;
                return (
                  <div key={subject}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {getSubjectDisplayName(subject)}
                      </span>
                      <span className="text-sm text-gray-600">{formatTime(time)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {Object.keys(timeData.bySubject).length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-2">⏱️</div>
          <p className="text-gray-600">No time data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Time tracking will appear as your child learns
          </p>
        </div>
      )}

      {/* Recommendations */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <span>💡</span>
          <span>Recommendations</span>
        </h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          {averageDaily < recommendedDaily && (
            <li>Encourage {formatTime(recommendedDaily - averageDaily)} more learning time per day</li>
          )}
          {Object.keys(timeData.bySubject).length < 3 && (
            <li>Try exploring more subjects for a well-rounded learning experience</li>
          )}
          {averageDaily >= recommendedDaily && (
            <li>Great job! Your child is meeting the recommended daily learning time</li>
          )}
        </ul>
      </div>
    </div>
  );
});

TimeManagement.displayName = 'TimeManagement';

export default TimeManagement;

